import { FormElementType } from '~graphql/generated/graphql'

export const FORM_RICH_TEXT_HIGHLIGHT_COLOR = '#FEFCBF'

export const FORM_RICH_TEXT_EDITOR_MODULES = {
    toolbar: [
        ['bold', 'italic'],
        [{ background: [FORM_RICH_TEXT_HIGHLIGHT_COLOR, false] }],
    ],
}

export const FORM_RICH_TEXT_EDITOR_FORMATS = ['bold', 'italic', 'background']

const FORM_RICH_TEXT_ELEMENT_TYPES: ReadonlySet<FormElementType> = new Set([
    FormElementType.Header,
    FormElementType.MediumHeader,
    FormElementType.Subtitle,
])

type RichTextStyle = {
    isBold?: boolean
    isItalic?: boolean
    isHighlighted?: boolean
}

type RichTextStyleStackItem = RichTextStyle & {
    tagName: string
}

export type RichTextSegment = RichTextStyle & {
    text: string
}

export function isFormRichTextElementType(
    type: FormElementType | null | undefined,
): boolean {
    return !!type && FORM_RICH_TEXT_ELEMENT_TYPES.has(type)
}

export function richTextToPlainText(content: string | null | undefined): string {
    if (!content) return ''
    return richTextToSegments(content)
        .map(segment => segment.text)
        .join('')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

export function hasRichTextFormatting(
    content: string | null | undefined,
): boolean {
    return !!content && /<\/?(strong|b|em|i|mark|span|p|br)\b/i.test(content)
}

export function richTextToSegments(
    content: string | null | undefined,
): RichTextSegment[] {
    if (!content) return []

    const segments: RichTextSegment[] = []
    const styleStack: RichTextStyleStackItem[] = []
    const tokens = content.match(/<[^>]+>|[^<]+/g)

    if (!tokens) return [{ text: content }]

    tokens.forEach(token => {
        if (!token.startsWith('<')) {
            const text = decodeHtmlEntities(token)
            if (!text) return

            const currentStyle = styleStack.reduce<RichTextStyle>(
                (acc, style) => ({
                    isBold: acc.isBold || style.isBold,
                    isItalic: acc.isItalic || style.isItalic,
                    isHighlighted: acc.isHighlighted || style.isHighlighted,
                }),
                {},
            )
            segments.push({ text, ...currentStyle })
            return
        }

        const tagName = getTagName(token)
        if (!tagName) return

        if (tagName === 'br') {
            segments.push({ text: '\n' })
            return
        }

        if (isClosingTag(token)) {
            if (tagName === 'p' || tagName === 'div' || tagName === 'li') {
                pushLineBreak(segments)
            }
            removeLastMatchingStyle(styleStack, tagName)
            return
        }

        const style = getStyleForTag(token, tagName)
        if (style) styleStack.push({ tagName, ...style })
    })

    return trimSegmentLineBreaks(segments)
}

function getTagName(tag: string): string | undefined {
    return tag.match(/^<\/?\s*([a-z0-9]+)/i)?.[1]?.toLowerCase()
}

function isClosingTag(tag: string): boolean {
    return /^<\//.test(tag)
}

function getStyleForTag(
    tag: string,
    tagName: string,
): RichTextStyle | undefined {
    const style: RichTextStyle = {}

    if (tagName === 'strong' || tagName === 'b') style.isBold = true
    if (tagName === 'em' || tagName === 'i') style.isItalic = true
    if (tagName === 'mark' || tagHasBackgroundColor(tag)) {
        style.isHighlighted = true
    }

    return Object.keys(style).length ? style : undefined
}

function tagHasBackgroundColor(tag: string): boolean {
    const styleMatch = tag.match(/\sstyle=(?:"([^"]*)"|'([^']*)')/i)
    const style = styleMatch?.[1] ?? styleMatch?.[2]
    if (!style) return false

    return /background-color\s*:\s*(?!transparent|inherit|initial|unset|none)/i.test(
        style,
    )
}

function removeLastMatchingStyle(
    styleStack: RichTextStyleStackItem[],
    tagName: string,
) {
    for (let index = styleStack.length - 1; index >= 0; index--) {
        if (styleStack[index].tagName === tagName) {
            styleStack.splice(index, 1)
            return
        }
    }
}

function pushLineBreak(segments: RichTextSegment[]) {
    const lastSegment = segments[segments.length - 1]
    if (!lastSegment || lastSegment.text.endsWith('\n')) return
    segments.push({ text: '\n' })
}

function trimSegmentLineBreaks(
    segments: RichTextSegment[],
): RichTextSegment[] {
    const nextSegments = [...segments]

    while (nextSegments[0]?.text === '\n') {
        nextSegments.shift()
    }

    while (nextSegments[nextSegments.length - 1]?.text === '\n') {
        nextSegments.pop()
    }

    return nextSegments
}

function decodeHtmlEntities(value: string): string {
    return value
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&#(\d+);/g, (_, codePoint: string) =>
            String.fromCharCode(Number(codePoint)),
        )
}
